/**
 * Paths into an upstream step's output, e.g. "issues.2.title".
 *
 * Mirrors lookup_result_field (task_processors/utils.py): a name segment reads an object
 * key, an integer segment picks one list element (0-based, negative counts from the end),
 * and a name segment applied to a list reads that key from every element.
 */

export type OutputValueType = 'string' | 'number' | 'boolean' | 'array' | 'object' | 'unknown';

// An agent's full raw text - always available, whatever its output_schema says.
export const RAW_RESPONSE_FIELD = 'response';

export interface OutputPathField {
  name: string;
  type: OutputValueType;
  description?: string;
}

type JsonSchema = Record<string, any>;

function schemaType(schema: JsonSchema | undefined): OutputValueType {
  if (!schema) return 'unknown';
  const t = Array.isArray(schema.type) ? schema.type.find((x: string) => x !== 'null') : schema.type;
  if (t === 'integer') return 'number';
  if (t === 'string' || t === 'number' || t === 'boolean' || t === 'array' || t === 'object') return t;
  if (schema.properties) return 'object';
  if (schema.items) return 'array';
  return 'unknown';
}

export function isIndexSegment(segment: string): boolean {
  return /^-?\d+$/.test(segment);
}

export function splitPath(path: string): string[] {
  return path ? path.split('.') : [];
}

/** One segment of a path, already resolved against the schema. */
export interface PathStep {
  // A key into an object.
  field?: string;
  // An index into the list `field` (or the previous step) produced; undefined = every element.
  index?: number;
  schema?: JsonSchema;
}

/**
 * Group a flat path into (field, optional index) steps, following the schema so each
 * step knows the subschema it lands on. "issues.2.title" -> [issues[2], title].
 */
export function parsePath(path: string, rootSchema: JsonSchema | undefined): PathStep[] {
  const steps: PathStep[] = [];
  let schema = rootSchema;
  for (const segment of splitPath(path)) {
    const last = steps[steps.length - 1];
    if (isIndexSegment(segment) && last && last.index === undefined && schemaType(last.schema) === 'array') {
      last.index = Number(segment);
      schema = last.schema?.items;
      continue;
    }
    // A key read on a list projects over every element - childSchema follows the items'.
    const child = childSchema(schema, segment);
    steps.push({ field: segment, schema: child });
    schema = child;
  }
  return steps;
}

export function joinPath(steps: PathStep[]): string {
  return steps
    .flatMap(s => (s.index === undefined ? [s.field ?? ''] : [s.field ?? '', String(s.index)]))
    .filter(Boolean)
    .join('.');
}

function objectSchemaOf(schema: JsonSchema | undefined): JsonSchema | undefined {
  return schemaType(schema) === 'array' ? schema?.items : schema;
}

/** The subschema of field `name` under `schema` (looking through arrays to their items). */
export function childSchema(schema: JsonSchema | undefined, name: string): JsonSchema | undefined {
  return objectSchemaOf(schema)?.properties?.[name];
}

/** The object fields selectable after `schema` (looking through arrays to their items). */
export function childFields(schema: JsonSchema | undefined): OutputPathField[] {
  const props = objectSchemaOf(schema)?.properties;
  if (!props || typeof props !== 'object') return [];
  return Object.entries(props).map(([name, sub]) => ({
    name,
    type: schemaType(sub as JsonSchema),
    description: (sub as JsonSchema)?.description,
  }));
}

export function stepType(step: PathStep): OutputValueType {
  const t = schemaType(step.schema);
  if (t === 'array' && step.index !== undefined) return schemaType(step.schema?.items);
  return t;
}

/** The type the full path resolves to, for picking condition operators. */
export function pathType(path: string, rootSchema: JsonSchema | undefined): OutputValueType {
  const steps = parsePath(path, rootSchema);
  if (!steps.length) return 'unknown';
  // Any un-indexed list before the last step projects the result into a list.
  if (steps.slice(0, -1).some(s => schemaType(s.schema) === 'array' && s.index === undefined)) return 'array';
  return stepType(steps[steps.length - 1]);
}

/** The schema of one element of the list at `path`, or undefined if the path isn't a known list. */
export function listItemSchema(path: string, rootSchema: JsonSchema | undefined): JsonSchema | undefined {
  const steps = parsePath(path, rootSchema);
  const last = steps[steps.length - 1];
  if (!last || last.index !== undefined || schemaType(last.schema) !== 'array') return undefined;
  return last.schema?.items;
}

export function ordinal(n: number): string {
  const v = n % 100;
  const suffix = v >= 11 && v <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
  return `${n}${suffix}`;
}

/** "issues › 3rd item › title" - for summaries next to a configured binding/variable. */
export function describePath(path: string): string {
  return splitPath(path)
    .map(segment => {
      if (!isIndexSegment(segment)) return segment;
      const i = Number(segment);
      return i === -1 ? 'last item' : i < 0 ? `${ordinal(-i)} from last` : `${ordinal(i + 1)} item`;
    })
    .join(' › ');
}

/** An upstream workflow step whose output a prompt variable can read, by backend step name. */
export interface UpstreamStep {
  stepName: string;
  label: string;
  agentId?: string;
}

// A data source wired to the workflow node an agent was opened from - the inputs its
// Single item / All items / Tracker schema variables can read.
export interface ConnectedInputInfo {
  name: string;
  label: string;
  // Tracker schema / outline sources - structured context, no items.
  itemless: boolean;
  // The data source node's task_type, e.g. data_source_outline.
  taskType?: string;
}
